import React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
    type ResetPasswordPayload,
    resetPasswordSchema,
    useResetPassword,
} from 'src/features/auth/api/useResetPassword';
import { loginMessages, registerMessages, resetPasswordMessages } from 'src/features/auth/utils';
import FormErrorBlock from 'src/registry/components/blocks/FormErrorBlock';
import { InputWithPassword } from 'src/registry/components/inputs/InputWithPassword';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Separator } from 'src/registry/ui/Separator';
import { generalMessages } from 'src/utils/messages';

const ResetPasswordForm = () => {
    const { formatMessage } = useIntl();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [success, setSuccess] = React.useState(false);

    const {
        mutate: resetPassword,
        error,
        isPending,
    } = useResetPassword({
        onSuccess: () => {
            setSuccess(true);
        },
    });

    const form = useForm<ResetPasswordPayload>({
        resolver: zodResolver(resetPasswordSchema),
        defaultValues: {
            password: '',
            confirm_password: '',
        },
    });

    const onSubmit = (values: ResetPasswordPayload) => {
        resetPassword({ ...values, token: searchParams.get('token') || '' });
    };

    React.useEffect(() => {
        if (success) {
            // Navigate the user after 5 seconds
            setTimeout(() => void navigate('/login'), 5000);
        }
    }, [success]);

    return (
        <>
            <div className="flex w-full max-w-sm flex-col items-center py-6 text-center">
                <h1 className="text-2xl font-semibold">{formatMessage(resetPasswordMessages.resetPassword)}</h1>
                <h3 className="text-center text-gray-500">
                    {formatMessage(resetPasswordMessages.resetPasswordMessage)}
                </h3>
            </div>

            <Form form={form} onSubmit={form.handleSubmit(onSubmit)} className="w-full max-w-sm">
                {!success && (
                    <>
                        <FormField
                            control={form.control}
                            name="password"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{formatMessage(resetPasswordMessages.newPassword)}</FormLabel>
                                    <FormControl>
                                        <InputWithPassword type="password" placeholder="••••••••" {...field} />
                                    </FormControl>
                                    <FormMessage>{formatMessage(registerMessages.registerPasswordTip)}</FormMessage>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="confirm_password"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{formatMessage(resetPasswordMessages.confirmNewPassword)}</FormLabel>
                                    <FormControl>
                                        <InputWithPassword type="password" placeholder="••••••••" {...field} />
                                    </FormControl>
                                    <FormMessage>
                                        {formatMessage(registerMessages.registerConfirmPasswordTip)}
                                    </FormMessage>
                                </FormItem>
                            )}
                        />
                        <Button className="w-full" type="submit" disabled={isPending || !form.formState.isValid}>
                            {formatMessage(generalMessages.submit)}
                        </Button>
                        <FormErrorBlock error={error} />
                    </>
                )}

                {success && (
                    <div className="flex flex-col gap-1 text-center text-sm">
                        <p className="font-semibold">{formatMessage(resetPasswordMessages.resetPasswordSuccess)}</p>
                        <p className="text-gray-500">
                            {formatMessage(resetPasswordMessages.resetPasswordSuccessDetails)}
                        </p>
                    </div>
                )}

                <div className="relative mt-4 flex flex-col gap-6">
                    <p className="absolute inset-x-0 -top-3 z-20 mx-auto w-12 bg-white p-1 text-center text-xs text-gray-500 uppercase">
                        <FormattedMessage {...generalMessages.or} />
                    </p>
                    <Separator />
                    <Link
                        to="/login"
                        className="text-primary-700 hover:text-primary-600 self-center text-center text-sm hover:underline"
                    >
                        <FormattedMessage {...loginMessages.loginMessage} />
                    </Link>
                </div>
            </Form>
        </>
    );
};

export { ResetPasswordForm };
